/**
 * Minimal Unicode Bidirectional Algorithm (UAX #9) for terminal display of mixed LTR/RTL text.
 *
 * The line editor stores text in LOGICAL order (first-typed char first) — correct for submit. But a raw-mode
 * TTY paints code units left-to-right, so a Hebrew/Arabic run shows reversed ("backwards"). This module
 * reorders a logical line into VISUAL order for rendering and returns a logical→visual caret map so the
 * cursor still lands at the right column.
 *
 * Scope: the IMPLICIT algorithm (rules W1–W7, N1–N2, I1–I2, L2) over a single embedding level — i.e. real
 * mixed Hebrew/Arabic/Latin/number input. Explicit embedding/override/isolate format chars (RLE/LRO/RLI…)
 * are essentially never typed into a chat CLI, so they're treated as neutral. Indices are UTF-16 code units
 * to match the editor's cursor space (BMP scripts like Hebrew are one unit each).
 */

type T = 'L' | 'R' | 'AL' | 'EN' | 'AN' | 'ES' | 'ET' | 'CS' | 'NSM' | 'WS' | 'ON';

/** Quick test: does the string contain any strong-RTL character? If not, skip bidi entirely (LTR fast path).
 *  Ranges mirror the R/AL set in classify() exactly: Hebrew, Arabic (+ supplements), and their presentation forms. */
const RTL_RE = /[\u0590-\u05ff\u0600-\u06ff\u0750-\u077f\u08a0-\u08ff\ufb1d-\ufdff\ufe70-\ufeff]/;
export const needsBidi = (s: string): boolean => RTL_RE.test(s);

/** Whether the APP should reorder RTL into visual order before writing to the TTY.
 *
 *  Terminal bidi support is inconsistent: Apple's Terminal.app reorders RTL itself, so app-side reordering
 *  would DOUBLE-flip it (Hebrew shows reversed). But iTerm2, VS Code's terminal, and most Linux terminals
 *  (gnome-terminal, xterm, alacritty, kitty) do NOT reorder — there the app MUST reorder or RTL shows
 *  reversed. So the default is auto: OFF on a terminal known to do its own bidi, ON everywhere else.
 *  `AGENTX_BIDI=1|0` (or true/false) forces it for terminals we guess wrong. */
const BIDI_TERMINALS = new Set(['Apple_Terminal']); // TERM_PROGRAM values that do their own RTL reordering
export const appBidi = (): boolean => {
  const env = process.env.AGENTX_BIDI?.toLowerCase();
  if (env === '1' || env === 'true' || env === 'on') return true;
  if (env === '0' || env === 'false' || env === 'off') return false;
  return !BIDI_TERMINALS.has(process.env.TERM_PROGRAM ?? '');
};

/** Bidi class of a single UTF-16 code unit (subset sufficient for typed input). */
function classify(c: number): T {
  if (c >= 0x0591 && c <= 0x05bd) return 'NSM';                 // Hebrew points (niqqud / cantillation)
  if (c === 0x05bf || c === 0x05c1 || c === 0x05c2 || c === 0x05c4 || c === 0x05c5 || c === 0x05c7) return 'NSM';
  if ((c >= 0x0590 && c <= 0x05ff) || (c >= 0xfb1d && c <= 0xfb4f)) return 'R'; // Hebrew letters + presentation
  if ((c >= 0x0660 && c <= 0x0669) || (c >= 0x06f0 && c <= 0x06f9)) return 'AN'; // Arabic-Indic digits
  if ((c >= 0x0600 && c <= 0x06ff) || (c >= 0x0750 && c <= 0x077f) || (c >= 0x08a0 && c <= 0x08ff) ||
      (c >= 0xfb50 && c <= 0xfdff) || (c >= 0xfe70 && c <= 0xfeff)) return 'AL'; // Arabic letters
  if (c >= 0x0030 && c <= 0x0039) return 'EN';                  // ASCII digits
  if (c === 0x002b || c === 0x002d) return 'ES';               // + -
  if (c === 0x0023 || c === 0x0024 || c === 0x0025 || (c >= 0x00a2 && c <= 0x00a5)) return 'ET'; // # $ % ¢£¤¥
  if (c === 0x002c || c === 0x002e || c === 0x003a || c === 0x002f || c === 0x00a0) return 'CS'; // , . : / NBSP
  if (c === 0x0020 || c === 0x0009 || c === 0x000b || c === 0x000c) return 'WS';
  if ((c >= 0x0021 && c <= 0x002f) || (c >= 0x003a && c <= 0x0040) ||
      (c >= 0x005b && c <= 0x0060) || (c >= 0x007b && c <= 0x007e)) return 'ON'; // ASCII punctuation/brackets
  return 'L';                                                  // Latin & everything else → strong LTR
}

/** P2/P3: paragraph base level from the first strong (L → 0, R/AL → 1; default LTR). */
function baseLevel(types: T[]): number {
  for (const t of types) { if (t === 'L') return 0; if (t === 'R' || t === 'AL') return 1; }
  return 0;
}

/** Rules W1–W7: resolve weak types within the single base-level run (sos = eos = base direction). */
function resolveWeak(t: T[], baseDir: 'L' | 'R'): T[] {
  const n = t.length;
  for (let i = 0; i < n; i++) if (t[i] === 'NSM') t[i] = i > 0 ? t[i - 1] : baseDir;          // W1
  for (let i = 0; i < n; i++) if (t[i] === 'EN') {                                            // W2
    for (let j = i - 1; j >= 0; j--) if (t[j] === 'L' || t[j] === 'R' || t[j] === 'AL') { if (t[j] === 'AL') t[i] = 'AN'; break; }
  }
  for (let i = 0; i < n; i++) if (t[i] === 'AL') t[i] = 'R';                                  // W3
  for (let i = 1; i < n - 1; i++) {                                                           // W4
    if (t[i] === 'ES' && t[i - 1] === 'EN' && t[i + 1] === 'EN') t[i] = 'EN';
    else if (t[i] === 'CS' && t[i - 1] === 'EN' && t[i + 1] === 'EN') t[i] = 'EN';
    else if (t[i] === 'CS' && t[i - 1] === 'AN' && t[i + 1] === 'AN') t[i] = 'AN';
  }
  for (let i = 0; i < n; i++) if (t[i] === 'ET') {                                            // W5
    let j = i; while (j < n && t[j] === 'ET') j++;
    if ((i > 0 && t[i - 1] === 'EN') || (j < n && t[j] === 'EN')) for (let k = i; k < j; k++) t[k] = 'EN';
    i = j - 1;
  }
  for (let i = 0; i < n; i++) if (t[i] === 'ES' || t[i] === 'ET' || t[i] === 'CS') t[i] = 'ON'; // W6
  for (let i = 0; i < n; i++) if (t[i] === 'EN') {                                            // W7
    let strong: T = baseDir;
    for (let j = i - 1; j >= 0; j--) if (t[j] === 'L' || t[j] === 'R') { strong = t[j]; break; }
    if (strong === 'L') t[i] = 'L';
  }
  return t;
}

/** Rules N1–N2: resolve neutrals (NI) — same-direction surround → that direction (EN/AN count as R), else base. */
function resolveNeutral(t: T[], baseDir: 'L' | 'R'): T[] {
  const n = t.length;
  const strong = (x: T): 'L' | 'R' | undefined => (x === 'L' ? 'L' : x === 'R' || x === 'EN' || x === 'AN' ? 'R' : undefined);
  for (let i = 0; i < n; i++) if (t[i] === 'ON' || t[i] === 'WS') {
    let j = i; while (j < n && (t[j] === 'ON' || t[j] === 'WS')) j++;
    const prev = i > 0 ? strong(t[i - 1]) : baseDir;
    const next = j < n ? strong(t[j]) : baseDir;
    const dir = prev && next && prev === next ? prev : baseDir;  // N1 else N2
    for (let k = i; k < j; k++) t[k] = dir;
    i = j - 1;
  }
  return t;
}

/** Rules I1–I2: implicit embedding level per resolved type. */
function implicitLevels(t: T[], e: number): number[] {
  return t.map((x) => e % 2 === 0
    ? (x === 'R' ? e + 1 : x === 'EN' || x === 'AN' ? e + 2 : e)        // even base
    : (x === 'L' || x === 'EN' || x === 'AN' ? e + 1 : e));            // odd base
}

/** Rule L2: reverse contiguous runs from the highest level down to the lowest odd level. */
function reorder(levels: number[]): number[] {
  const n = levels.length;
  const order = Array.from({ length: n }, (_, i) => i); // order[visualPos] = logicalIndex
  let highest = 0, lowestOdd = Infinity;
  for (const l of levels) { if (l > highest) highest = l; if (l % 2 && l < lowestOdd) lowestOdd = l; }
  for (let lvl = highest; lvl >= lowestOdd; lvl--) {
    let i = 0;
    while (i < n) {
      if (levels[i] >= lvl) {
        let j = i; while (j < n && levels[j] >= lvl) j++;
        for (let a = i, b = j - 1; a < b; a++, b--) { const tmp = order[a]; order[a] = order[b]; order[b] = tmp; }
        i = j;
      } else i++;
    }
  }
  return order;
}

export interface BidiLine {
  visual: string;    // the line reordered into visual (display) order
  caret: number[];   // caret[logicalIndex] = visual column (length len+1, includes end-of-line)
}

/** Reorder ONE logical line (no newlines) into visual order + a logical→visual caret map. */
export function bidiLine(line: string): BidiLine {
  const n = line.length;
  if (n === 0) return { visual: '', caret: [0] };
  const types = Array.from({ length: n }, (_, i) => classify(line.charCodeAt(i)));
  const e = baseLevel(types);
  const baseDir: 'L' | 'R' = e % 2 ? 'R' : 'L';
  const levels = implicitLevels(resolveNeutral(resolveWeak(types, baseDir), baseDir), e);
  const order = reorder(levels);
  const logToVis = new Array<number>(n);
  for (let v = 0; v < n; v++) logToVis[order[v]] = v;
  // Caret sits on the leading visual edge of a char whose run is LTR, the trailing edge if RTL.
  const caret = new Array<number>(n + 1);
  for (let c = 0; c < n; c++) caret[c] = levels[c] % 2 === 0 ? logToVis[c] : logToVis[c] + 1;
  caret[n] = levels[n - 1] % 2 === 0 ? logToVis[n - 1] + 1 : logToVis[n - 1];
  const visual = order.map((li) => line[li]).join('');
  return { visual, caret };
}

/**
 * Reorder a (possibly multi-line) logical buffer into visual order for display, mapping the logical cursor
 * to its visual column. Newlines are paragraph separators (each line reordered independently). Pure-LTR
 * buffers are returned untouched (fast path) so non-RTL behavior is byte-for-byte unchanged.
 */
export function applyBidi(buf: string, cursor: number): { text: string; cursor: number } {
  if (!needsBidi(buf)) return { text: buf, cursor };
  const lines = buf.split('\n');
  const out: string[] = [];
  let visCursor = cursor, logBase = 0, visBase = 0;
  for (const line of lines) {
    const { visual, caret } = bidiLine(line);
    out.push(visual);
    if (cursor >= logBase && cursor <= logBase + line.length) visCursor = visBase + caret[cursor - logBase];
    logBase += line.length + 1;  // + '\n'
    visBase += visual.length + 1;
  }
  return { text: out.join('\n'), cursor: visCursor };
}
